import {api} from './api'

interface IFilters {
    page?: number
    limit?: number
}

export interface IClassAdditionalInformation {
    fields: IAdditionalInformationField[]
}

export interface IAdditionalInformationField {
    id?: number
    title: string
    status: boolean
}

export interface IClassLocations {
    id: any
    shopify_location_id?: string
    name: string
    address_1: string
    address_2: string
    city: string
    state: string
    zip_code: string
    country: string
    timezone: string
    capacity: number
    created_at: string
    updated_at: string
    status: boolean
}

export interface IClass {
    id: string
    name: string
    shopify_variant_id: string
    time_zone: string
    truck_type: any
    minute_capacity: number
    additional_information_fields: IClassAdditionalInformation
    locations?: IClassLocations[]
    created_at: string
    updated_at: string
}

interface IListClassesResponse<T> {
    classes?: IClass[]
    records_count: number
    next_page: number | null
    previous_page: number | null
}

interface SearchVariantsRequest {
    query: string
    page?: number
}

interface SearchVariantsResponse {
    variants: Array<{
        id: number
        title: string
        price: number
    }>
    totalPages: number
}

export const classesApi = api.injectEndpoints({
    endpoints: (build) => ({
        getClasses: build.query<IListClassesResponse<IClass>, IFilters>({
            query: (params) => {
                return {url: `/classes`, params}
            }
        }),
        getClass: build.query<{ class: any }, string>({
            query: (classId) => {
                return {url: `/classes/${classId}`, method: 'GET'}
            }
        }),
        createClass: build.mutation<any, { body: Partial<any> }>({
            query: ({body}) => ({
                url: `/classes`,
                method: 'POST',
                body
            })
        }),
        updateClass: build.mutation<any, { classId: string; body: Partial<any> }>({
            query: ({classId, body}) => ({
                url: `/classes/${classId}`,
                method: 'PUT',
                body
            })
        }),
        deleteClass: build.mutation<{ class: any }, string>({
            query: (classId) => {
                return {url: `/classes/${classId}`, method: 'DELETE'}
            }
        }),
        searchVariants: build.mutation<
            SearchVariantsResponse,
            SearchVariantsRequest
        >({
            query: ({query}) => ({
                url: '/classes/variants',
                method: 'GET',
                params: {query}
            })
        }),
        getFeeProduct: build.mutation<any, void>({
            query: () => ({
                url: '/classes/variants',
                method: 'GET',
                params: {query: 'Fee'}
            })
        }),
        getTruckAvailability: build.query<
            any,
            { truckId: string; month: number; year: number }
        >({
            query: ({truckId, month, year}) => ({
                url: `/availability/truck`,
                method: 'GET',
                params: {truckId, month, year}
            })
        }),
        // Truck capacity overrides endpoints
        listAllTruckOverrides: build.query<any, void>({
            query: () => ({
                url: '/truckOverrides',
                method: 'GET'
            })
        }),
        getTruckOverride: build.query<any, string>({
            query: (id) => ({
                url: `/truckOverrides/${id}`,
                method: 'GET'
            })
        }),
        getTruckOverrideByLocationId: build.query<any, string>({
            query: (locationId) => ({
                url: `/truckOverrides/byLocation/${locationId}`,
                method: 'GET'
            })
        }),
        getTruckOverrideByTruckId: build.query<any, string>({
            query: (locationId) => ({
                url: `/truckOverrides/byTruckId/${locationId}`,
                method: 'GET'
            })
        }),
        createTruckCapacityOverride: build.mutation<any, { body: Partial<any> }>({
            query: ({body}) => ({
                url: '/truckOverrides',
                method: 'POST',
                body
            })
        }),
        updateTruckOverride: build.mutation<
            any,
            { id: string; body: Partial<any> }
        >({
            query: ({id, body}) => ({
                url: `/truckOverrides/${id}`,
                method: 'PUT',
                body
            })
        }),
        deleteTruckOverride: build.mutation<any, string>({
            query: (id) => ({
                url: `/truckOverrides/${id}`,
                method: 'DELETE'
            })
        }),
        getLocationAvailability: build.query<
            any,
            { locationId: string; month: number; year: number }
        >({
            query: ({locationId, month, year}: any) => ({
                url: `/availability/location`,
                method: 'GET',
                params: {locationId, month, year},
            }),
        }),
    }),

})

export const {
    useGetClassesQuery,
    useGetClassQuery,
    useCreateClassMutation,
    useUpdateClassMutation,
    useDeleteClassMutation,
    useSearchVariantsMutation,
    useGetFeeProductMutation,
    useGetTruckAvailabilityQuery,
    useListAllTruckOverridesQuery,
    useGetTruckOverrideQuery,
    useGetTruckOverrideByLocationIdQuery,
    useGetTruckOverrideByTruckIdQuery,
    useCreateTruckCapacityOverrideMutation,
    useUpdateTruckOverrideMutation,
    useDeleteTruckOverrideMutation,
    useGetLocationAvailabilityQuery, // Export the new query hook
} = classesApi
